import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { RoiId } from '~/domain/changes/ids';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { DomainError } from '~/domain/errors/DomainError';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { useRoiService } from './useRoiService';

export function useRoiCommands() {
    const service = useRoiService();
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);

    const handleError = useCallback((e: unknown) => {
        if (e instanceof DomainError) {
            enqueueSnackbar(e.message, { variant: 'warning' });
            return;
        }
        throw e;
    }, []);

    // Runs a domain mutation; on success arms the close/switch-patient
    // unsaved-changes guard (the legacy event handlers flip this atom, so
    // the command path must too, or the work is discarded with no warning).
    // Per-ROI review is locked on signed-off sets — RoiService throws
    // ReviewLockedError, which handleError surfaces without arming.
    const run = useCallback(
        (op: () => void) => {
            try {
                op();
                setHasUnsavedChanges(true);
            } catch (e) {
                handleError(e);
            }
        },
        [handleError, setHasUnsavedChanges]
    );

    const rename = useCallback(
        (id: RoiId, newName: string) => {
            run(() => service.rename({ id }, newName));
        },
        [run, service]
    );

    const setColor = useCallback(
        (id: RoiId, color: DicomColor) => {
            run(() => service.setColor({ id }, color));
        },
        [run, service]
    );

    const approve = useCallback(
        (id: RoiId, reviewer: string) => {
            run(() => service.approve({ id }, reviewer));
        },
        [run, service]
    );

    const reject = useCallback(
        (id: RoiId, reviewer: string) => {
            run(() => service.reject({ id }, reviewer));
        },
        [run, service]
    );

    const unapprove = useCallback(
        (id: RoiId) => {
            run(() => service.unapprove({ id }));
        },
        [run, service]
    );

    return { rename, setColor, approve, reject, unapprove };
}
